// Promotion-mode UI controller. It consumes only the normalized adapter
// contract, coordinates messages/modal/form ownership, and fails closed when
// configuration or provider state is unavailable.
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document && !root.CozyNookValueDealsPromotion) {
    root.CozyNookValueDealsPromotion = api;
    api.install(root, root.document);
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  const selector = '[data-home-value-deals-mode="kite_promotion"]';
  const instances = new Map();
  const pendingOwners = new Map();
  const cartReads = new WeakMap();

  // State precedence is intentional: login/config/loading failures are handled
  // before any unlocked action can be exposed.
  function resolveState(input) {
    const state = input || {};
    if (state.customerLoggedIn !== true) return 'login_required';
    if (state.configValid !== true) return 'unavailable';
    if (state.loading === true) return 'loading';
    if (state.requestFailed || state.kiteState === 'unavailable') return 'unavailable';
    if (state.hasEligibleItem === true) return 'added';
    if (state.adding === true) return 'adding';
    if (state.kiteState === 'locked') return 'locked';
    if (state.kiteState === 'unlocked') return 'unlocked';
    return 'unavailable';
  }

  // Aggregate duplicate variant lines before comparing server and rendered
  // state; discounted items may be split across multiple lines.
  function aggregate(items, idKey, quantityKey) {
    const quantities = new Map();
    Array.from(items || []).forEach(function (item) {
      const id = Number(item && item[idKey]);
      const quantity = Number(item && item[quantityKey]);
      if (!Number.isSafeInteger(id) || id <= 0 || !Number.isFinite(quantity) || quantity <= 0) return;
      quantities.set(id, (quantities.get(id) || 0) + quantity);
    });
    return Array.from(quantities.entries())
      .sort(function (left, right) { return left[0] - right[0]; })
      .map(function (entry) { return entry[0] + ':' + entry[1]; })
      .join('|');
  }

  function cartSignature(cart) { return aggregate(cart && cart.items, 'variant_id', 'quantity'); }

  function cartHasEligibleItem(cart, eligibleIds) {
    if (!cart || !Array.isArray(cart.items) || !(eligibleIds instanceof Set)) return false;
    return cart.items.some(function (item) {
      return eligibleIds.has(Number(item && item.variant_id)) && Number(item && item.quantity) > 0;
    });
  }

  function cartHasVariant(cart, variantId) {
    return Boolean(cart && Array.isArray(cart.items) && cart.items.some(function (item) {
      return Number(item && item.variant_id) === Number(variantId) && Number(item && item.quantity) > 0;
    }));
  }

  // Modal choices are built only from adapter-approved variants. Invalid
  // payloads are discarded instead of falling back to every product variant.
  function normalizeVariant(raw) {
    const id = Number(raw && raw.id);
    const available = raw && raw.available === true;
    const price = Number(raw && raw.price);
    const priceText = raw && typeof raw.priceText === 'string' ? raw.priceText : '';
    const options = raw && Array.isArray(raw.options) && raw.options.every(function (value) {
      return typeof value === 'string' && Boolean(value);
    }) ? raw.options.slice() : [];
    const sourceImage = raw && raw.image;
    const image = sourceImage && ['src', 'srcset', 'sizes'].every(function (key) {
      return typeof sourceImage[key] === 'string' && Boolean(sourceImage[key]);
    }) ? sourceImage : null;
    return {
      id: id,
      available: available,
      price: price,
      priceText: priceText,
      options: options,
      image: image,
      valid: Number.isSafeInteger(id) && id > 0 && available && Number.isSafeInteger(price) && price >= 0 && Boolean(priceText) && options.length > 0 && Boolean(image),
    };
  }

  function optionValueVisible(eligibleVariants, position, value) {
    return Array.from(eligibleVariants || []).some(function (variant) {
      return variant.valid && variant.options[position] === value;
    });
  }

  function optionValueCompatible(eligibleVariants, currentVariant, position, value) {
    return Array.from(eligibleVariants || []).some(function (variant) {
      if (!variant.valid || variant.options[position] !== value) return false;
      if (!currentVariant || !currentVariant.valid) return true;
      return variant.options.every(function (optionValue, index) {
        return index === position || optionValue === currentVariant.options[index];
      });
    });
  }

  function variantForOptionValue(eligibleVariants, currentVariant, position, value) {
    return Array.from(eligibleVariants || []).find(function (variant) {
      if (!variant.valid || variant.options[position] !== value) return false;
      if (!currentVariant || !currentVariant.valid) return true;
      return variant.options.every(function (optionValue, index) {
        return index === position || optionValue === currentVariant.options[index];
      });
    }) || null;
  }

  function resolveVariantAction(eligibleVariants) {
    const count = Array.isArray(eligibleVariants) ? eligibleVariants.length : 0;
    if (count === 1) return 'direct';
    if (count > 1) return 'modal';
    return 'unavailable';
  }

  function messageForState(config, current) {
    if (current === 'login_required') return config && config.loginMessage || '';
    if (current === 'added') return config && config.addedMessage || '';
    if (current === 'locked') return config && config.lockedMessage || '';
    return '';
  }

  function scheduleAddingRender(win, render) {
    const schedule = win && typeof win.setTimeout === 'function'
      ? win.setTimeout.bind(win)
      : setTimeout;
    schedule(render, 0);
  }

  function readInitialCart(win, cartUrl) {
    if (cartReads.has(win)) return cartReads.get(win);
    const request = win.fetch(cartUrl + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (response) { if (!response.ok) throw new Error('Cart request failed'); return response.json(); })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid Cart response');
        cartReads.delete(win);
        return cart;
      })
      .catch(function (error) { cartReads.delete(win); throw error; });
    cartReads.set(win, request);
    return request;
  }

  function findRoots(scope) {
    const roots = [];
    if (scope && scope.matches && scope.matches(selector)) roots.push(scope);
    if (scope && scope.querySelectorAll) scope.querySelectorAll(selector).forEach(function (root) { roots.push(root); });
    return roots;
  }

  function parseJson(node, fallback) {
    try { return JSON.parse(node ? node.textContent : ''); }
    catch (_error) { return fallback; }
  }

  // One controller instance owns one Section root. Re-initialization replaces
  // stale ownership cleanly in Shopify's theme editor.
  function init(root, win, doc) {
    const config = parseJson(root.querySelector('[data-home-value-deals-promotion-config]'), null);
    const sectionId = config && String(config.sectionId || '');
    if (!config || !sectionId || instances.has(sectionId)) return null;

    let eligibleIds = new Set();
    // The adapter validates these server-rendered candidates against Kite's
    // exact Y source before the controller can enable any Add action.
    const candidateProducts = Array.from(root.querySelectorAll('[data-home-value-deals-product-card]')).map(function (card) {
      const variants = parseJson(card.querySelector('[data-home-value-deals-variant-data]'), []);
      return {
        productId: card.dataset.productId,
        tags: parseJson(card.querySelector('[data-home-value-deals-product-tags]'), []),
        variantIds: variants.map(function (variant) { return variant && variant.id; }),
      };
    });
    const forms = Array.from(root.querySelectorAll('[data-home-value-deals-promotion-form]'));
    const modals = Array.from(root.querySelectorAll('basic-modal.home-value-deals__promotion-modal'));
    const messageTriggers = Array.from(root.querySelectorAll('[data-home-value-deals-promotion-message-trigger]'));
    const toast = root.querySelector('[data-home-value-deals-promotion-toast]');
    const toastMessage = toast && toast.querySelector('[data-home-value-deals-promotion-toast-message]');
    const toastAction = toast && toast.querySelector('[data-home-value-deals-promotion-toast-action]');
    const toastClose = toast && toast.querySelector('[data-home-value-deals-promotion-toast-close]');
    if (toast && toast.parentElement !== doc.body) doc.body.appendChild(toast);
    const formCards = new WeakMap();
    const variantCache = new WeakMap();
    forms.forEach(function (form) {
      const card = form.closest('[data-home-value-deals-product-card]');
      formCards.set(form, card);
      variantCache.set(form, parseJson(card && card.querySelector('[data-home-value-deals-variant-data]'), []));
    });
    const model = { cart: { items: [] }, kiteState: 'unavailable', loading: true, adding: false, requestFailed: false, refreshingAdapter: false, pendingVariantId: null };
    const cleanup = [];
    let adapter = null;
    let lastSignature = null;
    let pendingTimer = null;
    let toastTimer = null;
    let destroyed = false;

    function state() {
      return resolveState({
        customerLoggedIn: config.customerLoggedIn === true,
        configValid: config.configValid === true,
        loading: model.loading,
        requestFailed: model.requestFailed,
        kiteState: model.kiteState,
        hasEligibleItem: cartHasEligibleItem(model.cart, eligibleIds),
        adding: model.adding,
      });
    }

    function hideMessage() {
      if (toastTimer) (win.clearTimeout || clearTimeout)(toastTimer);
      toastTimer = null;
      if (toast) toast.hidden = true;
    }

    function showStateMessage(current) {
      const message = messageForState(config, current);
      if (!toast || !toastMessage || !message) return;
      if (toastTimer) (win.clearTimeout || clearTimeout)(toastTimer);
      toastMessage.textContent = message;
      if (toastAction) toastAction.hidden = current !== 'login_required';
      toast.hidden = false;
      toastTimer = (win.setTimeout || setTimeout)(hideMessage, current === 'login_required' ? 8000 : 4500);
    }

    function variantsFor(form) {
      return variantCache.get(form) || [];
    }

    function eligibleVariantsFor(form) {
      return variantsFor(form).map(normalizeVariant).filter(function (variant) {
        return variant.valid && eligibleIds.has(variant.id);
      });
    }

    function selectedVariant(form) {
      const variants = variantsFor(form);
      const input = form.querySelector('[data-home-value-deals-variant-id]');
      const selectedId = Number(input && input.value);
      return normalizeVariant(variants.find(function (variant) { return Number(variant.id) === selectedId; }));
    }

    function syncVariant(form) {
      const eligibleVariants = eligibleVariantsFor(form);
      const input = form.querySelector('[data-home-value-deals-variant-id]');
      let variant = selectedVariant(form);
      if (!variant.valid || !eligibleIds.has(variant.id)) variant = eligibleVariants[0] || normalizeVariant(null);
      if (input) input.value = variant.valid ? String(variant.id) : '';
      const card = formCards.get(form);
      const comparePrice = card && card.querySelector('[data-home-value-deals-promotion-compare-price]');
      const modal = form.closest('basic-modal') || (card && card.querySelector('basic-modal.home-value-deals__promotion-modal'));
      const modalComparePrice = modal && modal.querySelector('[data-home-value-deals-promotion-modal-compare-price]');
      const images = [];
      const cardImage = card && card.querySelector('.product-card__image--main img');
      const modalImage = modal && modal.querySelector('[data-home-value-deals-promotion-modal-image]');
      if (cardImage) images.push(cardImage);
      if (modalImage && modalImage !== cardImage) images.push(modalImage);
      if (variant.valid && comparePrice) comparePrice.textContent = variant.priceText;
      if (variant.valid && modalComparePrice) modalComparePrice.textContent = variant.priceText;
      if (variant.valid) images.forEach(function (image) {
        image.src = variant.image.src;
        image.srcset = variant.image.srcset;
        image.sizes = image.dataset.promotionImageSizes || variant.image.sizes;
        image.alt = variant.image.alt || '';
      });
      form.querySelectorAll('[data-home-value-deals-promotion-option]').forEach(function (option) {
        const position = Number(option.dataset.optionPosition) - 1;
        const value = option.dataset.optionValue || '';
        const visible = Number.isSafeInteger(position) && position >= 0 && optionValueVisible(eligibleVariants, position, value);
        const compatible = visible && optionValueCompatible(eligibleVariants, variant, position, value);
        const selected = visible && variant.valid && variant.options[position] === value;
        option.hidden = !visible;
        option.disabled = !compatible;
        option.setAttribute('aria-disabled', compatible ? 'false' : 'true');
        option.setAttribute('aria-pressed', selected ? 'true' : 'false');
        option.classList.toggle('is-selected', selected);
      });
      form.querySelectorAll('[data-home-value-deals-promotion-option-group]').forEach(function (group) {
        group.hidden = !group.querySelector('[data-home-value-deals-promotion-option]:not([hidden])');
      });
      return variant;
    }

    function render() {
      if (destroyed) return;
      const current = state();
      root.dataset.promotionState = current;
      // The controller only renders the Adapter state:
      // unavailable/loading = hidden or disabled; locked = grey message button;
      // unlocked = Product Form may submit; added = explanatory message.
      // It never reads Kite or calculates offer eligibility itself.
      // Logged-in customers remain hidden while eligibility is unresolved so
      // ineligible content never flashes before the adapter fails closed.
      const unresolvedCustomer = config.customerLoggedIn === true && current === 'loading';
      root.hidden = (current === 'unavailable' || unresolvedCustomer) && config.designMode !== true;
      if (current !== 'unlocked') modals.forEach(function (modal) { if (modal.open && typeof modal.hide === 'function') modal.hide(); });
      forms.forEach(function (form) {
        const button = form.querySelector('[data-home-value-deals-promotion-add]');
        const eligibleVariants = eligibleVariantsFor(form);
        const variant = syncVariant(form);
        const trigger = form.querySelector('[data-home-value-deals-promotion-trigger]');
        const addEnabled = current === 'unlocked' && variant.valid && eligibleVariants.length > 0;
        const messageEnabled = Boolean(trigger) && (current === 'locked' || current === 'added');
        const enabled = addEnabled || messageEnabled;
        if (button) {
          // Locked cards stay clickable for the explanatory message, but only
          // an explicitly unlocked card is allowed to submit its product form.
          if (trigger) button.type = addEnabled ? 'submit' : 'button';
          button.disabled = !enabled;
          button.setAttribute('aria-disabled', enabled ? 'false' : 'true');
        }
        if (trigger) {
          trigger.setAttribute('aria-haspopup', addEnabled && eligibleVariants.length > 1 ? 'dialog' : 'false');
        }
      });
    }

    function clearPending(failed) {
      const key = model.pendingVariantId === null ? '' : String(model.pendingVariantId);
      if (pendingTimer) (win.clearTimeout || clearTimeout)(pendingTimer);
      pendingTimer = null;
      if (key && pendingOwners.get(key) === sectionId) pendingOwners.delete(key);
      model.pendingVariantId = null;
      model.adding = false;
      if (failed === true) model.requestFailed = true;
    }

    function acceptCart(cart) {
      if (destroyed || !cart || !Array.isArray(cart.items)) return;
      const signature = cartSignature(cart);
      if (model.pendingVariantId !== null && cartHasVariant(cart, model.pendingVariantId)) clearPending(false);
      const shouldRenderRecovery = model.requestFailed || model.loading;
      model.requestFailed = false;
      model.loading = false;
      if (signature === lastSignature) {
        if (shouldRenderRecovery) render();
        return;
      }
      lastSignature = signature;
      model.cart = cart;
      if (adapter) {
        model.refreshingAdapter = true;
        model.kiteState = adapter.refresh(cart);
        model.refreshingAdapter = false;
      }
      render();
    }

    function addListener(target, eventName, callback, options) {
      target.addEventListener(eventName, callback, options);
      cleanup.push(function () { target.removeEventListener(eventName, callback, options); });
    }

    messageTriggers.forEach(function (trigger) {
      addListener(trigger, 'click', function () { showStateMessage(state()); });
    });
    if (toastClose) addListener(toastClose, 'click', hideMessage);

    if (config.customerLoggedIn !== true || config.configValid !== true) {
      model.loading = false;
      render();
    } else {
      const adapterApi = win.CozyNookKiteOfferAdapter;
      if (!adapterApi || adapterApi.version !== 2) {
        model.loading = false;
        model.requestFailed = true;
        render();
      } else {
        try {
          adapter = adapterApi.create({
            offerId: config.offerId,
            yRuleType: config.yRuleType,
            collectionId: config.collectionId,
            productTag: config.productTag,
            candidateProducts: candidateProducts,
            window: win,
            document: doc,
          });
          cleanup.push(adapter.subscribe(function (nextState) {
            if (destroyed) return;
            model.kiteState = nextState;
            eligibleIds = new Set(adapter.getEligibleVariantIds());
            if (!model.refreshingAdapter) render();
          }));
          cleanup.push(function () { adapter.destroy(); });

          const cartUrl = win.FoxTheme && win.FoxTheme.routes && win.FoxTheme.routes.cart_url || '/cart';
          readInitialCart(win, cartUrl).then(acceptCart).catch(function () {
            if (destroyed) return;
            model.loading = false;
            model.requestFailed = true;
            render();
          });

          const pubsub = win.FoxTheme && win.FoxTheme.pubsub;
          const cartEvent = pubsub && pubsub.PUB_SUB_EVENTS && pubsub.PUB_SUB_EVENTS.cartUpdate;
          const cartError = pubsub && pubsub.PUB_SUB_EVENTS && pubsub.PUB_SUB_EVENTS.cartError;
          if (pubsub && typeof pubsub.subscribe === 'function') {
            if (cartEvent) cleanup.push(pubsub.subscribe(cartEvent, function (payload) { acceptCart(payload && payload.cart); }));
            if (cartError) cleanup.push(pubsub.subscribe(cartError, function (payload) {
              const productVariantId = Number(payload && payload.productVariantId);
              if (productVariantId !== model.pendingVariantId || pendingOwners.get(String(productVariantId)) !== sectionId) return;
              clearPending(true);
              render();
            }));
          }
          addListener(doc, 'cart:updated', function (event) { acceptCart(event.detail && event.detail.cart); });
        } catch (_error) {
          model.loading = false;
          model.requestFailed = true;
          render();
        }
      }
    }

    forms.forEach(function (form) {
      form.querySelectorAll('[data-home-value-deals-promotion-option]').forEach(function (option) {
        addListener(option, 'click', function () {
          const position = Number(option.dataset.optionPosition) - 1;
          const value = option.dataset.optionValue || '';
          const nextVariant = variantForOptionValue(eligibleVariantsFor(form), selectedVariant(form), position, value);
          if (!nextVariant || !eligibleIds.has(nextVariant.id)) return;
          const input = form.querySelector('[data-home-value-deals-variant-id]');
          if (input) input.value = String(nextVariant.id);
          render();
        });
      });

      const trigger = form.querySelector('[data-home-value-deals-promotion-trigger]');
      if (trigger) addListener(trigger, 'click', function (event) {
        const eligibleVariants = eligibleVariantsFor(form);
        if (state() !== 'unlocked' || eligibleVariants.length === 0) {
          event.preventDefault();
          showStateMessage(state());
          return;
        }
        if (resolveVariantAction(eligibleVariants) === 'direct') return;
        event.preventDefault();
        const modal = doc.getElementById(trigger.dataset.promotionModalId || '');
        if (!modal || typeof modal.show !== 'function') return;
        const modalForm = modal.querySelector('[data-home-value-deals-promotion-form="modal"]');
        const modalInput = modalForm && modalForm.querySelector('[data-home-value-deals-variant-id]');
        if (modalInput) modalInput.value = String(eligibleVariants[0].id);
        render();
        modal.show(trigger);
      });

      addListener(form, 'submit', function (event) {
        const variant = selectedVariant(form);
        const ownerKey = String(variant.id);
        if (state() !== 'unlocked' || !variant.valid || !eligibleIds.has(variant.id) || (pendingOwners.has(ownerKey) && pendingOwners.get(ownerKey) !== sectionId)) {
          event.preventDefault();
          return;
        }
        const modal = form.closest('basic-modal');
        if (modal && typeof modal.hide === 'function') modal.hide();
        pendingOwners.set(ownerKey, sectionId);
        model.pendingVariantId = variant.id;
        model.adding = true;
        pendingTimer = (win.setTimeout || setTimeout)(function () {
          if (destroyed || pendingOwners.get(ownerKey) !== sectionId) return;
          clearPending(true);
          render();
        }, 15000);
        scheduleAddingRender(win, function () {
          if (!destroyed) render();
        });
      }, true);
    });

    const instance = { destroy: function () {
      if (destroyed) return;
      destroyed = true;
      clearPending(false);
      hideMessage();
      cleanup.splice(0).reverse().forEach(function (dispose) { dispose(); });
      modals.forEach(function (modal) {
        if (modal.parentElement === doc.body) modal.remove();
      });
      if (toast && toast.parentElement === doc.body) toast.remove();
      instances.delete(sectionId);
    } };
    instances.set(sectionId, instance);
    return instance;
  }

  function initAll(scope, win, doc) { findRoots(scope).forEach(function (root) { init(root, win, doc); }); }
  function destroyAll(scope) {
    findRoots(scope).forEach(function (root) {
      const config = parseJson(root.querySelector('[data-home-value-deals-promotion-config]'), {});
      const instance = instances.get(String(config.sectionId || ''));
      if (instance) instance.destroy();
    });
  }

  function install(win, doc) {
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { initAll(doc, win, doc); }, { once: true });
    else initAll(doc, win, doc);
    doc.addEventListener('shopify:section:load', function (event) { initAll(event.target, win, doc); });
    doc.addEventListener('shopify:section:unload', function (event) { destroyAll(event.target); });
  }

  return Object.freeze({ version: 2, instances: instances, pendingOwners: pendingOwners, resolveState: resolveState, messageForState: messageForState, cartSignature: cartSignature, cartHasEligibleItem: cartHasEligibleItem, cartHasVariant: cartHasVariant, normalizeVariant: normalizeVariant, optionValueVisible: optionValueVisible, optionValueCompatible: optionValueCompatible, variantForOptionValue: variantForOptionValue, resolveVariantAction: resolveVariantAction, scheduleAddingRender: scheduleAddingRender, readInitialCart: readInitialCart, createInstance: init, install: install });
});
